import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@/types/api';

export type UserRole = 'admin' | 'teacher' | 'student' | 'parent';

interface ImpersonatedUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
}

interface ImpersonationState {
  isImpersonating: boolean;
  effectiveRole: UserRole;
  actualRole: UserRole;
  impersonatedUser?: ImpersonatedUser;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  // Impersonation state
  impersonation: ImpersonationState | null;
  login: (user: User, token: string) => void;
  logout: () => void;
  // Impersonation actions
  startImpersonation: (token: string, impersonatingRole: UserRole, impersonatedUser: ImpersonatedUser) => void;
  stopImpersonation: (token: string) => void;
  // Helper to get effective role (accounts for impersonation)
  getEffectiveRole: () => UserRole | null;
  // Helper to get impersonated user info
  getImpersonatedUser: () => ImpersonatedUser | null;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      impersonation: null,
      login: (user, token) => {
        set({ user, token, isAuthenticated: true, impersonation: null });
      },
      logout: () => {
        set({ user: null, token: null, isAuthenticated: false, impersonation: null });
      },
      startImpersonation: (token, impersonatingRole, impersonatedUser) => {
        const { user } = get();
        if (!user || user.role !== 'admin') return;

        set({
          token,
          impersonation: {
            isImpersonating: true,
            effectiveRole: impersonatingRole,
            actualRole: 'admin',
            impersonatedUser,
          },
        });
      },
      stopImpersonation: (token) => {
        set({
          token,
          impersonation: null,
        });
      },
      getEffectiveRole: () => {
        const { user, impersonation } = get();
        if (!user) return null;
        return impersonation?.effectiveRole ?? user.role;
      },
      getImpersonatedUser: () => {
        const { impersonation } = get();
        return impersonation?.impersonatedUser ?? null;
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        impersonation: state.impersonation,
      }),
    }
  )
);
